import Phaser from 'phaser';
import { NoteGraphic } from './StaffLine/Note';
import { PhraseGraphic } from './StaffLine';

/**
 * Tracks error feedback visuals (phantom notes and bars) per phrase.
 * Manages lifecycle and cleanup of spawned error feedback elements.
 */
export class ErrorFeedbackManager {
  private scene: Phaser.Scene;
  private feedbackByPhrase: Map<string, {
    notes: NoteGraphic[];
    notesByOriginalId: Map<string, NoteGraphic>; // Map original note ID to phantom note
    bars: Phaser.GameObjects.Graphics[];
    noteOnPositions: Map<string, number>; // Track note-on cursor positions by note ID
  }> = new Map();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Register a phantom note for a phrase.
   * @param phraseUuid - UUID of the phrase
   * @param note - The phantom note graphic
   * @param originalNoteId - ID of the original note this phantom represents
   */
  addPhantomNote(phraseUuid: string, note: NoteGraphic, originalNoteId: string): void {
    if (!this.feedbackByPhrase.has(phraseUuid)) {
      this.feedbackByPhrase.set(phraseUuid, {
        notes: [],
        notesByOriginalId: new Map(),
        bars: [],
        noteOnPositions: new Map(),
      });
    }
    const feedback = this.feedbackByPhrase.get(phraseUuid)!;
    feedback.notes.push(note);
    feedback.notesByOriginalId.set(originalNoteId, note);
  }

  /**
   * Register a duration bar for a phrase.
   */
  addPhantomBar(phraseUuid: string, bar: Phaser.GameObjects.Graphics): void {
    if (!this.feedbackByPhrase.has(phraseUuid)) {
      this.feedbackByPhrase.set(phraseUuid, {
        notes: [],
        notesByOriginalId: new Map(),
        bars: [],
        noteOnPositions: new Map(),
      });
    }
    this.feedbackByPhrase.get(phraseUuid)!.bars.push(bar);
  }

  /**
   * Track the cursor position when a note was pressed (for offset bar calculation).
   */
  setNoteOnPosition(phraseUuid: string, noteId: string, cursorX: number): void {
    if (!this.feedbackByPhrase.has(phraseUuid)) {
      this.feedbackByPhrase.set(phraseUuid, {
        notes: [],
        notesByOriginalId: new Map(),
        bars: [],
        noteOnPositions: new Map(),
      });
    }
    this.feedbackByPhrase.get(phraseUuid)!.noteOnPositions.set(noteId, cursorX);
  }

  /**
   * Get the cursor position when a note was pressed.
   */
  getNoteOnPosition(phraseUuid: string, noteId: string): number | undefined {
    return this.feedbackByPhrase.get(phraseUuid)?.noteOnPositions.get(noteId);
  }

  /**
   * Get the phantom note for a given original note ID (for attaching offset bars).
   */
  getPhantomNote(phraseUuid: string, originalNoteId: string): NoteGraphic | undefined {
    const feedback = this.feedbackByPhrase.get(phraseUuid);
    if (!feedback) return undefined;
    return feedback.notesByOriginalId.get(originalNoteId);
  }

  /**
   * Clean up all feedback for a specific phrase.
   */
  cleanupPhrase(phraseUuid: string): void {
    const feedback = this.feedbackByPhrase.get(phraseUuid);
    if (!feedback) return;

    feedback.notes.forEach(note => note.destroy());
    feedback.bars.forEach(bar => bar.destroy());
    feedback.noteOnPositions.clear();

    this.feedbackByPhrase.delete(phraseUuid);
  }

  /**
   * Clean up all feedback for all phrases.
   */
  cleanupAll(): void {
    this.feedbackByPhrase.forEach((feedback, phraseUuid) => {
      this.cleanupPhrase(phraseUuid);
    });
    this.feedbackByPhrase.clear();
  }

  /**
   * Destroy the manager and clean up all resources.
   */
  destroy(): void {
    this.cleanupAll();
  }
}
